import { userTypeName } from './user-type';
import { enqueueSnackbar, SnackbarMessage } from 'notistack';

export function cleanAmountInput(input: string, allowNegative?: boolean): string {
    if (allowNegative) {
        // Remove all except digits, period, +, -
        input = input.replace(/[^\d.+-]/g, '');

        // Only allow one leading + or -
        input = input.replace(/^(\+|-)?(.*)/, (m, sign, rest) => {
            rest = rest.replace(/[+-]/g, ''); // remove any other + or -
            return (sign || '') + rest;
        });
    } else {
        // Remove all except digits, period
        input = input.replace(/[^\d.]/g, '');
    }

    // Only allow one period
    let parts = input.split('.');
    if (parts.length > 1) {
        input = parts.shift() + '.' + parts.join('').replace(/\./g, '');
    }

    // Allow up to two digits after the period
    input = input.replace(/^([+-]?\d*)(\.(\d{0,2})).*$/, (m, intPart, dotAndDec, decPart) => {
        return intPart + (dotAndDec || '');
    });

    return input;
}

export function clipboardSetText(text: string) {
    navigator.clipboard.writeText(text);
    snack(`"${text}" copied to clipboard`, { variant: 'info' });
}

export function boundary2LngLat(boundary: any) {
    return boundary.map((l: string, i: number) => ({ lng: l[0], lat: l[1] }));
}

export function exportCSV(data: any, filename: string) {
    const csv = data
        .map((row: any) => row.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
    link.remove();
}

export function getBanners(): Record<string, string> {
    return {
        '1': 'Just Listed',
        '2': 'Just Reduced',
        '3': 'Pending',
        '4': 'Sold',
        '5': 'Vacation Rental',
        '6': 'Pocket Listing',
        '7': 'Coming Soon',
        '8': 'Back on the Market',
        '9': 'Rented',
        '10': 'Rental',
        '11': 'Multi-Family',
        '12': 'For Lease',
        '13': 'Auction',
        '14': 'In Contract',
        '15': 'AirBnB',
        '16': 'Luxury Listing',
        '17': 'Business Opportunity',
        '18': 'Land for Sale',
        '19': 'Similar Home Shown',
        '20': 'For Sale',
        '21': 'Move-in Ready',
        '22': 'Waterfront',
        '23': 'Water Views',
        '24': 'New to Market',
        '25': 'Pre-Construction',
        '26': 'New Construction',
        '27': 'Beach House',
        '28': 'Pool'
    };
}

/** Maps legacy banner IDs to v3 API `banner` enum values. */
export const bannerIdToEnum: Record<string, string> = {
    '1': 'justlisted',
    '2': 'justreduced',
    '3': 'pending',
    '4': 'sold',
    '5': 'vacationrental',
    '6': 'pocketlisting',
    '7': 'comingsoon',
    '8': 'backonthemarket',
    '9': 'rented',
    '10': 'rental',
    '11': 'multifamily',
    '12': 'forlease',
    '13': 'auction',
    '14': 'incontract',
    '15': 'airbnb',
    '16': 'luxurylisting',
    '17': 'businessopportunity',
    '18': 'landforsale',
    '19': 'similarhomeshown',
    '20': 'forsale',
    '21': 'moveinready',
    '22': 'waterfront',
    '23': 'waterviews',
    '24': 'newtomarket',
    '25': 'preconstruction',
    '26': 'newconstruction',
    '27': 'beachhouse',
    '28': 'pool'
};

export function getColor(i: number): string {
    const colors = [
        '#3366CC',
        '#DC3912',
        '#FF9900',
        '#109618',
        '#990099',
        '#3B3EAC',
        '#0099C6',
        '#DD4477',
        '#66AA00',
        '#B82E2E',
        '#316395',
        '#994499',
        '#22AA99',
        '#AAAA11',
        '#6633CC',
        '#E67300',
        '#8B0707',
        '#329262',
        '#5574A6',
        '#3B3EAC'
    ];
    return colors[i % colors.length];
}

export function getStates(): Record<string, string> {
    return {
        AL: 'Alabama',
        AK: 'Alaska',
        AZ: 'Arizona',
        AR: 'Arkansas',
        CA: 'California',
        CO: 'Colorado',
        CT: 'Connecticut',
        DE: 'Delaware',
        DC: 'District Of Columbia',
        FL: 'Florida',
        GA: 'Georgia',
        HI: 'Hawaii',
        ID: 'Idaho',
        IL: 'Illinois',
        IN: 'Indiana',
        IA: 'Iowa',
        KS: 'Kansas',
        KY: 'Kentucky',
        LA: 'Louisiana',
        ME: 'Maine',
        MD: 'Maryland',
        MA: 'Massachusetts',
        MI: 'Michigan',
        MN: 'Minnesota',
        MS: 'Mississippi',
        MO: 'Missouri',
        MT: 'Montana',
        NE: 'Nebraska',
        NV: 'Nevada',
        NH: 'New Hampshire',
        NJ: 'New Jersey',
        NM: 'New Mexico',
        NY: 'New York',
        NC: 'North Carolina',
        ND: 'North Dakota',
        OH: 'Ohio',
        OK: 'Oklahoma',
        OR: 'Oregon',
        PA: 'Pennsylvania',
        RI: 'Rhode Island',
        SC: 'South Carolina',
        SD: 'South Dakota',
        TN: 'Tennessee',
        TX: 'Texas',
        UT: 'Utah',
        VT: 'Vermont',
        VA: 'Virginia',
        WA: 'Washington',
        WV: 'West Virginia',
        WI: 'Wisconsin',
        WY: 'Wyoming'
    };
}

export function hasRole(roles: string[], role: string): boolean {
    return roles.indexOf(role) !== -1;
}

// ROBO-726 — moved to its own module so the Hubby core can use it without this file's imports.
export { isValidEmail } from './email-address';

export function mediaSize(sizes: string, width: number): string | null {
    if (!sizes) {
        return null;
    }
    return (
        sizes.split(',').find(size => parseInt(size.split(':')[1]) >= width) || sizes.split(',')[0]
    ).split(':')[0];
}

export function setBodyClass(className: string, include: boolean) {
    if (document.body.className.split(' ').indexOf(className) === -1) {
        if (include) {
            document.body.classList.add(className);
        }
    } else {
        if (!include) {
            document.body.classList.remove(className);
        }
    }
}

export function snack(message: SnackbarMessage, options: any) {
    enqueueSnackbar(message, options);
}

export function titleCase(str: string): string {
    return str.replace(
        /\w\S*/g,
        word => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()
    );
}

// Defined in user-type.ts (no notistack import) and re-exported here so every caller is unaffected.
export { userTypeName };

interface MediaOptions {
    width?: number;
    height?: number;
    quality?: number;
}

/**
 * Gets a fully formatted media URL based on the provided media string and options
 * @param media - The media string containing image variants (e.g. 'h/tx2s9lddtx:87:43:38`19`62`70,...')
 * @param ext - The file extension (e.g. 'jpg', 'png')
 * @param options - Optional configuration for the media URL
 * @returns Formatted media URL or empty string if media is invalid
 */
export function getMediaUrl(
    media?: string | null,
    ext?: string | null,
    options: MediaOptions = {}
): string {
    if (!media || !ext) {
        return '';
    }

    const baseUrl = 'https://media.hd.pics/';
    const mediaId = mediaSize(media, options.width || 100);

    if (!mediaId) {
        return '';
    }

    let url = `${baseUrl}${mediaId}.${ext}`;

    // Add optional parameters
    const params: string[] = [];
    if (options.width) params.push(`w=${options.width}`);
    if (options.height) params.push(`h=${options.height}`);
    if (options.quality) params.push(`q=${options.quality}`);

    if (params.length > 0) {
        url += `?${params.join('&')}`;
    }

    return url;
}
